/**
 * Phase 6 Tier 1 (Feb 2026) — Pending-order audio alert hook.
 *
 * When invoked with a positive `pendingCount`, plays a short alert tone via
 * Web Audio API every CHIME_INTERVAL_MS, stopping automatically when the
 * count returns to zero.
 *
 * Browser quirks handled:
 *   - Browsers block AudioContext.resume() until a user-gesture has fired.
 *     We expose an `armed` flag + an `arm()` helper; the dispatcher dashboard
 *     shows a one-time "ENABLE AUDIO ALERTS" button to satisfy this. Once
 *     armed, the context stays alive for the session.
 *   - Audio is also gated by `enabled` (user toggle) and `pendingCount > 0`.
 *
 * Tone generated synthetically (no asset download): two short 880Hz beeps
 * with a 100ms gap. Pleasant enough not to be hated, loud enough to notice.
 *
 * Usage in DispatchDashboard:
 *   const audio = usePendingOrderAudio(pendingCount, { enabled: alertOn });
 *   {!audio.armed && <Button onClick={audio.arm}>ENABLE AUDIO ALERTS</Button>}
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const CHIME_INTERVAL_MS = 30_000; // play every 30s

export function usePendingOrderAudio(pendingCount, { enabled = true } = {}) {
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);
  const [armed, setArmed] = useState(false);

  const ensureContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctxRef.current = new AC();
    return ctxRef.current;
  }, []);

  const playChime = useCallback(async () => {
    const ctx = ensureContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch (_) { return; }
    }
    const now = ctx.currentTime;
    // Two short pulses — first 880Hz, second 880Hz, 100ms apart
    [0, 0.18].forEach((t) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0, now + t);
      gain.gain.linearRampToValueAtTime(0.25, now + t + 0.02);
      gain.gain.linearRampToValueAtTime(0, now + t + 0.13);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.14);
    });
  }, [ensureContext]);

  // User-gesture arming. Call once when the user clicks the enable button.
  const arm = useCallback(async () => {
    const ctx = ensureContext();
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') await ctx.resume();
      // Play a near-silent ping so iOS marks the context as "user-activated"
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine'; osc.frequency.value = 600;
      gain.gain.setValueAtTime(0.001, now);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now); osc.stop(now + 0.05);
      setArmed(true);
      // Persist the user's choice so the button only shows once per session
      try { sessionStorage.setItem('pending_audio_armed', '1'); } catch (_e) { /* sessionStorage may be disabled */ }
    } catch (_e) {
      // ignore — audio context errored mid-arming
    }
  }, [ensureContext]);

  // Restore armed state on remount (e.g., dashboard re-render)
  useEffect(() => {
    try {
      if (sessionStorage.getItem('pending_audio_armed') === '1') {
        setArmed(true);
      }
    } catch (_e) { /* sessionStorage may be disabled */ }
  }, []);

  // The chime loop
  useEffect(() => {
    if (!enabled || !armed || !pendingCount || pendingCount <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }
    // Fire one immediately, then every CHIME_INTERVAL_MS
    playChime();
    intervalRef.current = setInterval(playChime, CHIME_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, armed, pendingCount, playChime]);

  // Cleanup the context on unmount
  useEffect(() => () => {
    if (ctxRef.current) {
      try { ctxRef.current.close(); } catch (_e) { /* already closed */ }
      ctxRef.current = null;
    }
  }, []);

  return { armed, arm };
}
